import * as THREE from 'three';

/**
 * Finds the closest line segment to a given mouse position within a threshold
 * @param {THREE.Scene} scene - The Three.js scene to search
 * @param {THREE.Vector3} mousePos - The mouse position in world coordinates
 * @param {number} threshold - The maximum distance to consider (default: 20)
 * @returns {Object|null} Object containing closest line info or null if none found
 */
export function findClosestLine(scene, mousePos, threshold = 20) {
  let closest = null;
  let minDistance = threshold;

  scene.traverse((object) => {
    if (object.isLine) {
      if (object.userData.isPanelPart) return;

      const geometry = object.geometry;
      if (!geometry) return;

      const positionAttr = geometry.attributes?.position;
      if (!positionAttr || !positionAttr.array) return;

      const positions = positionAttr.array;

      for (let i = 0; i < positions.length - 3; i += 3) {
        const start = new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]);
        const end = new THREE.Vector3(positions[i + 3], positions[i + 4], positions[i + 5]);

        const closestPoint = closestPointOnLineSegment(start, end, mousePos);
        const dist = mousePos.distanceTo(closestPoint);

        if (dist < minDistance) {
          minDistance = dist;
          closest = { object, start, end, distance: dist };
        }
      }
    }
  });

  return closest;
}

/**
 * Finds the closest point on a line segment to a given point, with an offset distance
 * @param {THREE.Vector3} start - Start point of the line segment
 * @param {THREE.Vector3} end - End point of the line segment
 * @param {THREE.Vector3} point - The point to find closest position to
 * @param {number} offset - Distance to offset the result point from the line segment
 * @returns {THREE.Vector3} The closest point on the line segment, offset by the specified distance
 */
export function closestPointOnLineSegment(start, end, point, offset = 0) {
  const dir = new THREE.Vector3().subVectors(end, start);
  const len = dir.length();
  dir.normalize();

  const v = new THREE.Vector3().subVectors(point, start);
  const d = v.dot(dir);

  let closestPoint;
  if (d < 0) {
    closestPoint = start.clone();
  } else if (d > len) {
    closestPoint = end.clone();
  } else {
    closestPoint = new THREE.Vector3().addVectors(start, dir.multiplyScalar(d));
  }

  // If no offset is needed, return the closest point
  if (offset === 0) {
    return closestPoint;
  }

  // Calculate the offset direction (from line segment to the query point)
  const offsetDir = new THREE.Vector3().subVectors(point, closestPoint);
  
  // If the query point is exactly on the line segment, we can't determine offset direction
  if (offsetDir.length() === 0) {
    // Return the closest point without offset since direction is ambiguous
    return closestPoint;
  }
  
  // Normalize and apply offset
  offsetDir.normalize();
  return closestPoint.addVectors(closestPoint, offsetDir.multiplyScalar(offset));
}